import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Grid,
  Paper,
  Button,
  Dialog,
  Divider,
  MenuItem,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { Add, Edit, Delete, Sync, Visibility, CloudUpload } from "@material-ui/icons";

import toJs from "../../hoc/toJS";
import select from "../../utils/select";
import { getAllDepartments } from "../../reducer/department/action";
import { uploadFile } from "../../reducer/file/action";
import { notification } from "../../reducer/home/action";
import FilePreviewModal from "../../containers/File/FilePreviewModal";
import {
  getAllDocument,
  insertDocument,
  updateDocument,
  deleteDocument,
} from "../../reducer/document/action";

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

// [VinUni] Module 8 - Quản lý văn bản

const DOCUMENT_TYPES = [
  { value: "quyetdinh", label: "Quyết định" },
  { value: "thongbao", label: "Thông báo" },
  { value: "congvan", label: "Công văn" },
  { value: "quydinh", label: "Quy định" },
  { value: "huongdan", label: "Hướng dẫn" },
  { value: "khac", label: "Khác" },
];

const emptyForm = {
  id: null,
  title: "",
  documentNumber: "",
  documentType: "quyetdinh",
  issuedBy: "",
  issuedDate: "",
  departmentId: "",
  fileUrl: "",
  description: "",
};

const typeLabel = (value) => DOCUMENT_TYPES.find((t) => t.value === value)?.label || value || "";
const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("vi-VN");
};

function Document(props) {
  const { role, documents, departments, isFetching } = props;

  const canManage = role === "sudo-admin" || role === "humanresource";

  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [form, setForm] = useState(emptyForm);
  const [openForm, setOpenForm] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [file, setFile] = useState(null); // file PDF mới chọn
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null); // url để xem preview

  useEffect(() => {
    props.getAllDocument();
    if (!departments || departments.length === 0) props.getAllDepartments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const departmentName = useMemo(() => {
    const map = {};
    (departments || []).forEach((d) => {
      map[d.id] = d.name;
    });
    return map;
  }, [departments]);

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return (documents || []).filter((doc) => {
      const matchKw =
        !kw ||
        (doc.title || "").toLowerCase().includes(kw) ||
        (doc.documentNumber || "").toLowerCase().includes(kw) ||
        (doc.issuedBy || "").toLowerCase().includes(kw);
      const matchDept = departmentFilter === "all" || String(doc.departmentId) === String(departmentFilter);
      return matchKw && matchDept;
    });
  }, [documents, search, departmentFilter]);

  const handleOpenInsert = () => {
    setForm(emptyForm);
    setFile(null);
    setFileError("");
    setOpenForm(true);
  };

  const handleOpenEdit = (doc) => {
    setFile(null);
    setFileError("");
    setForm({
      id: doc.id,
      title: doc.title || "",
      documentNumber: doc.documentNumber || "",
      documentType: doc.documentType || "quyetdinh",
      issuedBy: doc.issuedBy || "",
      issuedDate: doc.issuedDate ? new Date(doc.issuedDate).toISOString().slice(0, 10) : "",
      departmentId: doc.departmentId || "",
      fileUrl: doc.fileUrl || "",
      description: doc.description || "",
    });
    setOpenForm(true);
  };

  const handleChange = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSelectFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      setFileError("Chỉ chấp nhận file PDF.");
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setFileError("Dung lượng file phải dưới 25MB.");
      setFile(null);
      return;
    }
    setFileError("");
    setFile(f);
  };

  const buildPayload = () => ({
    title: form.title,
    documentNumber: form.documentNumber,
    documentType: form.documentType,
    issuedBy: form.issuedBy,
    issuedDate: form.issuedDate ? new Date(form.issuedDate).toISOString() : null,
    departmentId: form.departmentId ? Number(form.departmentId) : 0,
    fileUrl: form.fileUrl,
    description: form.description,
  });

  const handleSubmit = async () => {
    if (!form.title.trim() || fileError) return;
    setSubmitting(true);
    const payload = buildPayload();

    // 1) Tạo/cập nhật văn bản
    const res = form.id ? await props.updateDocument(form.id, payload) : await props.insertDocument(payload);
    if (!res?.success) {
      setSubmitting(false);
      return;
    }
    const docId = res.data?.id || form.id;

    // 2) Nếu có chọn file PDF mới -> upload rồi gán fileUrl
    if (file && docId) {
      const up = await props.uploadFile(docId, "document", [file]);
      if (up?.success) {
        await props.updateDocument(docId, { ...payload, fileUrl: up.data });
      }
    }

    props.notification({
      type: "success",
      message: form.id ? "Cập nhật văn bản thành công!" : "Thêm văn bản thành công!",
    });
    props.getAllDocument();
    setSubmitting(false);
    setOpenForm(false);
    setFile(null);
  };

  const fileFullUrl = (url) => (url ? `${process.env.REACT_APP_DOMAIN}/api/v1${url}` : null);

  const handleConfirmDelete = () => {
    if (deleting) props.deleteDocument(deleting.id);
    setDeleting(null);
  };

  return (
    <Box style={{ padding: "16px 0" }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <Typography variant="h5">Quản lý văn bản</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Tìm kiếm (tiêu đề, số hiệu...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Đơn vị"
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
          >
            <MenuItem value="all">Tất cả đơn vị</MenuItem>
            {(departments || []).map((d) => (
              <MenuItem key={d.id} value={String(d.id)}>
                {d.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<Sync />}
              style={{ marginRight: 8 }}
              onClick={() => props.getAllDocument()}
            >
              Đồng bộ
            </Button>
            {canManage && (
              <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenInsert}>
                Thêm
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      <Divider style={{ margin: "12px 0" }} />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>STT</TableCell>
              <TableCell>Tiêu đề</TableCell>
              <TableCell>Số hiệu</TableCell>
              <TableCell>Loại</TableCell>
              <TableCell>Ngày ban hành</TableCell>
              <TableCell>Cơ quan ban hành</TableCell>
              <TableCell>Đơn vị</TableCell>
              <TableCell>File</TableCell>
              {canManage && <TableCell align="right">Tác vụ</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={canManage ? 9 : 8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 9 : 8} align="center">
                  Chưa có văn bản.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((doc, index) => (
                <TableRow key={doc.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{doc.title}</TableCell>
                  <TableCell>{doc.documentNumber}</TableCell>
                  <TableCell>{typeLabel(doc.documentType)}</TableCell>
                  <TableCell>{formatDate(doc.issuedDate)}</TableCell>
                  <TableCell>{doc.issuedBy}</TableCell>
                  <TableCell>{doc.department?.name || departmentName[doc.departmentId] || ""}</TableCell>
                  <TableCell>
                    {doc.fileUrl ? (
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        startIcon={<Visibility fontSize="small" />}
                        onClick={() => setPreview(doc.fileUrl)}
                      >
                        Xem
                      </Button>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(doc)} title="Sửa">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="secondary" onClick={() => setDeleting(doc)} title="Xoá">
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Form thêm/sửa */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? "Cập nhật văn bản" : "Thêm văn bản"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                size="small"
                variant="outlined"
                label="Tiêu đề văn bản"
                value={form.title}
                onChange={handleChange("title")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Số hiệu"
                value={form.documentNumber}
                onChange={handleChange("documentNumber")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Loại văn bản"
                value={form.documentType}
                onChange={handleChange("documentType")}
              >
                {DOCUMENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                variant="outlined"
                label="Ngày ban hành"
                InputLabelProps={{ shrink: true }}
                value={form.issuedDate}
                onChange={handleChange("issuedDate")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Cơ quan ban hành"
                value={form.issuedBy}
                onChange={handleChange("issuedBy")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label="Đơn vị"
                value={form.departmentId}
                onChange={handleChange("departmentId")}
              >
                <MenuItem value="">-- Không gán đơn vị --</MenuItem>
                {(departments || []).map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                  {file ? "Đổi file PDF" : "Tải lên file PDF"}
                  <input hidden type="file" accept="application/pdf,.pdf" onChange={handleSelectFile} />
                </Button>
                {file && (
                  <Typography variant="body2" style={{ color: "#2e548a" }}>
                    {file.name} ({(file.size / 1024 / 1024).toFixed(1)}MB)
                  </Typography>
                )}
                {!file && form.fileUrl && (
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    startIcon={<Visibility fontSize="small" />}
                    onClick={() => setPreview(form.fileUrl)}
                  >
                    Xem file hiện tại
                  </Button>
                )}
              </Box>
              {fileError ? (
                <Typography variant="caption" color="error">
                  {fileError}
                </Typography>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  Chỉ nhận PDF, dung lượng dưới 25MB.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                variant="outlined"
                label="Trích yếu / Mô tả"
                value={form.description}
                onChange={handleChange("description")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} disabled={submitting}>
            Huỷ
          </Button>
          <Button color="primary" variant="contained" onClick={handleSubmit} disabled={submitting || !!fileError}>
            {submitting ? "Đang lưu..." : form.id ? "Cập nhật" : "Thêm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Xem trước file PDF */}
      <FilePreviewModal open={!!preview} url={preview} handleClose={() => setPreview(null)} />

      {/* Xác nhận xoá */}
      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>Xác nhận xoá</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc muốn xoá văn bản <b>{deleting?.title}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Huỷ</Button>
          <Button color="secondary" variant="contained" onClick={handleConfirmDelete}>
            Xoá
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  role: select(state, "userReducer", "role"),
  documents: select(state, "documentReducer", "list"),
  isFetching: select(state, "documentReducer", "isFetching"),
  isSubmitting: select(state, "documentReducer", "isSubmitting"),
  departments: select(state, "departmentReducer", "departments"),
});

const mapDispatchToProps = (dispatch) => ({
  getAllDocument: () => dispatch(getAllDocument()),
  getAllDepartments: () => dispatch(getAllDepartments()),
  insertDocument: (payload, cleanUp) => dispatch(insertDocument(payload, cleanUp)),
  updateDocument: (id, payload, cleanUp) => dispatch(updateDocument(id, payload, cleanUp)),
  deleteDocument: (id) => dispatch(deleteDocument(id)),
  uploadFile: (id, type, files) => dispatch(uploadFile(id, type, files)),
  notification: (payload) => dispatch(notification(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(Document));
